import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { db } from '../../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';
import './BrandEnquiryCart.css';

const BrandEnquiryCart = ({ brand, slug }) => {
  const { cart, cartOpen, setCartOpen, removeFromCart, updateQty, clearCart, totalItems, totalPrice } = useCart();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const primary = brand?.primaryColor || '#111';

  useEffect(() => {
    if (!cartOpen || !slug) return;
    const fetchStores = async () => {
      try {
        const snap = await getDocs(collection(db, 'brands', slug, 'stores'));
        setStores(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { setStores([]); }
    };
    fetchStores();
  }, [cartOpen, slug]);

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    if (!selectedStore) { toast.error('Please select a store'); return; }
    if (cart.length === 0) { toast.error('Your enquiry is empty'); return; }

    setSubmitting(true);
    try {
      const settingsSnap = await getDocs(collection(db, 'brands', slug, 'settings'));
      const config = {};
      settingsSnap.docs.forEach(d => { config[d.id] = d.data().value; });

      const store = stores.find(s => s.id === selectedStore);
      const itemsList = cart.map(i => `• ${i.name} x${i.qty} — $${(i.price * i.qty).toFixed(2)}`).join('\n');

      const params = {
        customer_name: name,
        customer_email: email,
        customer_phone: phone || 'Not provided',
        store_name: store?.name || '',
        store_email: store?.email || '',
        brand_name: brand?.name || '',
        items_list: itemsList,
        total_price: `$${totalPrice.toFixed(2)}`,
        comment: comment || 'No additional comments',
        reply_to: email,
      };

      if (config?.serviceId && config?.templateId && config?.publicKey) {
        await emailjs.send(config.serviceId, config.templateId, params, config.publicKey);
        toast.success('Enquiry sent! Check your email.');
        setSubmitted(true);
        clearCart();
        setComment(''); setSelectedStore(''); setName(''); setEmail(''); setPhone('');
      } else {
        toast.error('Email not configured yet. Please contact the store directly.');
      }
    } catch (e) {
      toast.error('Failed to send. Please try again.');
    }
    setSubmitting(false);
  };

  const handleClose = () => { setCartOpen(false); if (submitted) setSubmitted(false); };

  if (!cartOpen) return null;

  return (
    <>
      <div className="bec-overlay" onClick={handleClose} />
      <div className="bec-panel">
        <div className="bec-header" style={{ background: primary }}>
          <div>
            <h2>Your Enquiry</h2>
            {totalItems > 0 && <span className="bec-count">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>}
          </div>
          <button className="bec-close" onClick={handleClose}>✕</button>
        </div>

        {submitted ? (
          <div className="bec-success">
            <div className="bec-success-icon" style={{ background: `${primary}15` }}>
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={primary} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="16,8 10,14 8,12"/></svg>
            </div>
            <h3>Enquiry Sent!</h3>
            <p>We've emailed the store and CC'd you. They'll be in touch soon!</p>
            <button className="bec-continue" onClick={handleClose} style={{ background: primary, borderColor: primary }}>Continue Browsing</button>
          </div>
        ) : (
          <div className="bec-body">
            {cart.length === 0 ? (
              <div className="bec-empty">
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
                <p>No items yet</p>
                <button className="btn-outline" onClick={handleClose}>Browse Menu</button>
              </div>
            ) : (
              <>
                <div className="bec-items">
                  {cart.map(item => (
                    <div key={item.id} className="bec-item">
                      {item.imageUrl && <img src={item.imageUrl} alt={item.name} className="bec-item-img" />}
                      <div className="bec-item-info">
                        <span className="bec-item-name">{item.name}</span>
                        <span className="bec-item-price">${(item.price * item.qty).toFixed(2)}</span>
                      </div>
                      <div className="bec-item-controls">
                        <button onClick={() => updateQty(item.id, item.qty - 1)}>−</button>
                        <span>{item.qty}</span>
                        <button onClick={() => updateQty(item.id, item.qty + 1)}>+</button>
                        <button className="bec-remove" onClick={() => removeFromCart(item.id)}>🗑</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bec-total" style={{ background: primary }}>
                  <span>Estimated Total</span>
                  <span className="bec-total-price">${totalPrice.toFixed(2)}</span>
                </div>

                <div className="bec-form">
                  <p className="bec-form-title">Your Details</p>
                  <input className="form-input" placeholder="Your name *" value={name} onChange={e => setName(e.target.value)} />
                  <input className="form-input" type="email" placeholder="Your email *" value={email} onChange={e => setEmail(e.target.value)} />
                  <input className="form-input" placeholder="Phone (optional)" value={phone} onChange={e => setPhone(e.target.value)} />

                  <p className="bec-form-title">Select Store</p>
                  <select className="form-input" value={selectedStore} onChange={e => setSelectedStore(e.target.value)}>
                    <option value="">— Choose a store *</option>
                    {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {stores.length === 0 && <p className="bec-hint">No stores configured yet.</p>}

                  <p className="bec-form-title">Additional Comments</p>
                  <textarea className="form-input" placeholder="Event date, number of guests, dietary requirements, delivery address..." value={comment} onChange={e => setComment(e.target.value)} rows={4} />
                </div>

                <button className="bec-submit" onClick={handleSubmit} disabled={submitting} style={{ background: primary, borderColor: primary }}>
                  {submitting ? 'Sending...' : 'Submit Enquiry →'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BrandEnquiryCart;
